import { LOCALE_INTL_TAGS, type Locale } from '@nk/shared';

/**
 * Dates, numbers and durations — phases.md §1.12 and §1.15.
 *
 * Everything goes through `Intl` with the active locale's BCP-47 tag, which is what makes
 * an Assamese page render ৫ মিনিট rather than "5 min": Assamese uses Bengali numerals,
 * and hand-formatting a number is how a bilingual site ends up half-translated.
 *
 * Timestamps are stored UTC and displayed in `Asia/Kolkata` (§1.12) — the newsroom's own
 * clock, not the reader's, so a story filed at 9pm in Guwahati reads 9pm everywhere.
 */
const TIME_ZONE = 'Asia/Kolkata';

const dateFormatters = new Map<string, Intl.DateTimeFormat>();

function dateFormatter(locale: Locale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}:${JSON.stringify(options)}`;
  let formatter = dateFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(LOCALE_INTL_TAGS[locale], {
      timeZone: TIME_ZONE,
      ...options,
    });
    dateFormatters.set(key, formatter);
  }
  return formatter;
}

/** `21 May 2026` — the dateline under every card. */
export function formatDate(iso: string | null, locale: Locale): string {
  if (!iso) return '';
  return dateFormatter(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(iso),
  );
}

/** `21 May 2026, 9:14 pm` — used where the hour matters, as on a live timeline. */
export function formatDateTime(iso: string | null, locale: Locale): string {
  if (!iso) return '';
  return dateFormatter(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** `9:14 pm` — a live update's own stamp. */
export function formatTime(iso: string | null, locale: Locale): string {
  if (!iso) return '';
  return dateFormatter(locale, { hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(LOCALE_INTL_TAGS[locale]).format(value);
}

/** `4:21` for a video's duration label. Digits follow the locale, the colon does not. */
export function formatDuration(seconds: number | null, locale: Locale): string {
  if (!seconds || seconds <= 0) return '';
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  const pad = formatNumber(rest, locale).padStart(2, formatNumber(0, locale));
  return `${formatNumber(minutes, locale)}:${pad}`;
}

/**
 * `4 minutes ago` — a live timeline's own clock.
 *
 * `Intl.RelativeTimeFormat` rather than a hand-written ladder of strings: it is the one
 * piece of this that has to be right in two languages, and Assamese plural rules are not
 * something to reimplement in `if` statements (§1.15). The unit is chosen the way a
 * person would say it, largest that still reads as a count.
 */
export function formatRelativeTime(iso: string | null, locale: Locale, now = Date.now()): string {
  if (!iso) return '';
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  const magnitude = Math.abs(seconds);

  const formatter = new Intl.RelativeTimeFormat(LOCALE_INTL_TAGS[locale], { numeric: 'auto' });
  if (magnitude < 60) return formatter.format(Math.round(seconds), 'second');
  if (magnitude < 3600) return formatter.format(Math.round(seconds / 60), 'minute');
  if (magnitude < 86_400) return formatter.format(Math.round(seconds / 3600), 'hour');
  return formatter.format(Math.round(seconds / 86_400), 'day');
}
